import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { 
  Star, 
  Eye,
  EyeOff,
  Trash2,
  Search,
  Filter,
  Loader2,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  ThumbsUp,
  ThumbsDown,
  MoreVertical,
  Flag
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AdminReviewManagement - Admin panel for managing customer reviews
 * Features: View, Hide, Delete, Filter, Search
 */
const AdminReviewManagement = ({ country = 'SL' }) => {
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, visible, hidden
  const [filterRating, setFilterRating] = useState('all'); // all, 1, 2, 3, 4, 5
  const [sortBy, setSortBy] = useState('newest'); // newest, oldest, highest, lowest
  
  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [country]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [reviewsRes, statsRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/review-management/admin/all?limit=100`),
        axios.get(`${API_URL}/api/review-management/admin/stats`),
        axios.get(`${API_URL}/api/review-management/admin/settings/${country}`),
      ]);
      
      setReviews(reviewsRes.data.reviews || []);
      setStats(statsRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to fetch review data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleReviewVisibility = async (reviewId, currentVisible) => {
    try {
      await axios.put(`${API_URL}/api/review-management/admin/${reviewId}/visibility`, {
        visible: !currentVisible
      });
      
      setReviews(reviews.map(r => 
        r.id === reviewId ? { ...r, visible: !currentVisible } : r
      ));
    } catch (error) {
      console.error('Failed to toggle visibility:', error);
      alert('Failed to update review visibility');
    }
  };

  const deleteReview = async () => {
    if (!reviewToDelete) return;
    
    try {
      setDeleting(true);
      await axios.delete(`${API_URL}/api/review-management/admin/${reviewToDelete.id}`);
      
      setReviews(reviews.filter(r => r.id !== reviewToDelete.id));
      setShowDeleteModal(false);
      setReviewToDelete(null);
    } catch (error) {
      console.error('Failed to delete review:', error);
      alert('Failed to delete review');
    } finally {
      setDeleting(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSavingSettings(true);
      await axios.post(`${API_URL}/api/review-management/admin/settings`, {
        ...settings,
        country
      });
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // Filter and sort reviews
  const filteredReviews = reviews
    .filter(review => {
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        if (
          !review.customer_name?.toLowerCase().includes(search) &&
          !review.review_text?.toLowerCase().includes(search) &&
          !review.service_name?.toLowerCase().includes(search)
        ) {
          return false;
        }
      }
      
      // Status filter
      if (filterStatus === 'visible' && review.visible === false) return false;
      if (filterStatus === 'hidden' && review.visible !== false) return false;
      
      // Rating filter
      if (filterRating !== 'all' && review.rating !== parseInt(filterRating)) return false;
      
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'highest':
          return b.rating - a.rating;
        case 'lowest':
          return a.rating - b.rating;
        case 'newest':
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });

  const renderStars = (rating) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-4 w-4 ${star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
        />
      ))}
    </div>
  );

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-review-management">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Star className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_reviews || 0}</p>
                <p className="text-xs text-gray-500">Total Reviews</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Star className="h-5 w-5 text-yellow-600 fill-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.average_rating?.toFixed(1) || '0.0'}</p>
                <p className="text-xs text-gray-500">Avg Rating</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Eye className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.visible_reviews || 0}</p>
                <p className="text-xs text-gray-500">Visible</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <EyeOff className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.hidden_reviews || 0}</p>
                <p className="text-xs text-gray-500">Hidden</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <ThumbsUp className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.recommendation_rate || 0}%</p>
                <p className="text-xs text-gray-500">Recommend</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Review Settings</CardTitle>
          <CardDescription>Configure review collection and display</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Enable Reviews</p>
                <p className="text-xs text-gray-500">Allow customers to submit reviews</p>
              </div>
              <Switch
                checked={settings?.reviews_enabled ?? true}
                onCheckedChange={(v) => setSettings({ ...settings, reviews_enabled: v })}
              />
            </div>
            
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Auto-Approve Reviews</p>
                <p className="text-xs text-gray-500">Show reviews without manual approval</p>
              </div>
              <Switch
                checked={settings?.auto_approve ?? true}
                onCheckedChange={(v) => setSettings({ ...settings, auto_approve: v })}
              />
            </div>
            
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Feedback Page</p>
                <p className="text-xs text-gray-500">Enable /feedback/{'{token}'} page</p>
              </div>
              <Switch
                checked={settings?.feedback_page_enabled ?? true}
                onCheckedChange={(v) => setSettings({ ...settings, feedback_page_enabled: v })}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Minimum Rating to Display</label>
              <Select
                value={String(settings?.min_display_rating || 1)}
                onValueChange={(v) => setSettings({ ...settings, min_display_rating: parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Show all (1+ stars)</SelectItem>
                  <SelectItem value="2">2+ stars only</SelectItem>
                  <SelectItem value="3">3+ stars only</SelectItem>
                  <SelectItem value="4">4+ stars only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium">Reviews Per Page</label>
              <Select
                value={String(settings?.reviews_per_page || 10)}
                onValueChange={(v) => setSettings({ ...settings, reviews_per_page: parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5 reviews</SelectItem>
                  <SelectItem value="10">10 reviews</SelectItem>
                  <SelectItem value="20">20 reviews</SelectItem>
                  <SelectItem value="50">50 reviews</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <Button onClick={saveSettings} disabled={savingSettings}>
            {savingSettings ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save Settings
          </Button>
        </CardContent>
      </Card>

      {/* Reviews List Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Manage Reviews</CardTitle>
              <CardDescription>View, hide, or delete customer reviews</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search reviews..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="visible">Visible</SelectItem>
                <SelectItem value="hidden">Hidden</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filterRating} onValueChange={setFilterRating}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Rating" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Ratings</SelectItem>
                <SelectItem value="5">5 Stars</SelectItem>
                <SelectItem value="4">4 Stars</SelectItem>
                <SelectItem value="3">3 Stars</SelectItem>
                <SelectItem value="2">2 Stars</SelectItem>
                <SelectItem value="1">1 Star</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="oldest">Oldest</SelectItem>
                <SelectItem value="highest">Highest</SelectItem>
                <SelectItem value="lowest">Lowest</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Reviews List */}
          {filteredReviews.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Star className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No reviews found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredReviews.map((review) => (
                <div 
                  key={review.id}
                  className={`p-4 border rounded-lg transition-all ${
                    review.visible === false ? 'bg-gray-50 opacity-60' : 'bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {renderStars(review.rating)}
                        <Badge variant="outline" className="text-xs">
                          {review.verified_booking ? 'Verified' : 'Unverified'}
                        </Badge>
                        {review.visible === false && (
                          <Badge className="bg-gray-200 text-gray-600 text-xs">Hidden</Badge>
                        )}
                        {review.would_recommend && (
                          <Badge className="bg-green-100 text-green-700 text-xs">
                            <ThumbsUp className="h-3 w-3 mr-1" />
                            Recommends
                          </Badge>
                        )}
                      </div>
                      
                      <p className="font-medium">{review.customer_name}</p>
                      <p className="text-sm text-gray-500">
                        {review.service_name} • {new Date(review.created_at).toLocaleDateString()}
                      </p>
                      
                      {review.review_text && (
                        <p className="text-gray-700 mt-2 text-sm">{review.review_text}</p>
                      )}
                      
                      <p className="text-xs text-gray-400 mt-2">
                        Provider: {review.provider_name || review.provider_id}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleReviewVisibility(review.id, review.visible !== false)}
                        title={review.visible === false ? 'Show review' : 'Hide review'}
                      >
                        {review.visible === false ? (
                          <Eye className="h-4 w-4" />
                        ) : (
                          <EyeOff className="h-4 w-4" />
                        )}
                      </Button>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          setReviewToDelete(review);
                          setShowDeleteModal(true);
                        }}
                        title="Delete review"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {filteredReviews.length > 0 && (
            <p className="text-sm text-gray-500 mt-4 text-center">
              Showing {filteredReviews.length} of {reviews.length} reviews
            </p>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Review
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete this review? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {reviewToDelete && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                {renderStars(reviewToDelete.rating)}
              </div>
              <p className="font-medium">{reviewToDelete.customer_name}</p>
              {reviewToDelete.review_text && (
                <p className="text-sm text-gray-600 mt-1">&quot;{reviewToDelete.review_text}&quot;</p>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={deleteReview}
              disabled={deleting}
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminReviewManagement;
